import { TrackerEventType } from "src/core/model/TrackerEventType";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";

import { EventListener } from "./EventListener";
import { NativeEventTrigger } from "./PageViewListener";

/**
 * Triggers FORM_INTERACTION event on form focus-in and submit.
 * Uses cooldown to prevent excessive triggering when user tabs
 * between multiple form fields rapidly.
 */
export class FormInteractionListener implements EventListener {
  private trigger: NativeEventTrigger;
  private started = false;
  private boundFocusHandler: ((e: FocusEvent) => void) | null = null;
  private boundSubmitHandler: ((e: SubmitEvent) => void) | null = null;
  private cooldownMs: number;
  private lastTriggerTs = 0;

  constructor(trigger: NativeEventTrigger, cooldownMs = 3000) {
    this.trigger = trigger;
    this.cooldownMs = cooldownMs;
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.boundFocusHandler = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const isFormElement =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement;

      if (isFormElement) {
        this.triggerWithCooldown();
      }
    };

    this.boundSubmitHandler = () => {
      this.triggerWithCooldown();
    };

    document.addEventListener("focusin", this.boundFocusHandler, { passive: true });
    document.addEventListener("submit", this.boundSubmitHandler, { passive: true });
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    if (this.boundFocusHandler) {
      document.removeEventListener("focusin", this.boundFocusHandler);
      this.boundFocusHandler = null;
    }
    if (this.boundSubmitHandler) {
      document.removeEventListener("submit", this.boundSubmitHandler);
      this.boundSubmitHandler = null;
    }
  }

  getTypes(): TrackerEventType[] {
    return [TrackerEventType.FORM_INTERACTION];
  }

  getVariableTypes(): TrackerVariableType[] {
    return [TrackerVariableType.FORM_FILL_ANOMALY, TrackerVariableType.FORM_FILL_DURATION_MS];
  }

  private triggerWithCooldown(): void {
    const now = Date.now();
    if (now - this.lastTriggerTs >= this.cooldownMs) {
      this.lastTriggerTs = now;
      this.trigger(TrackerEventType.FORM_INTERACTION);
    }
  }
}
